const DELIVERY_METHOD = 'shipping';
const GUEST_CART_STORAGE_KEY = 'wearby.guestCart';

const isObject = value => value && typeof value === 'object' && !Array.isArray(value);

const isPositiveInteger = value => Number.isInteger(value) && value > 0;

const isValidPriceSnapshot = priceSnapshot => {
  return (
    isObject(priceSnapshot) &&
    Number.isInteger(priceSnapshot.amount) &&
    typeof priceSnapshot.currency === 'string'
  );
};

const cleanupCart = cart => {
  return Object.entries(cart || {}).reduce((sellerCart, [sellerId, deliveryBuckets]) => {
    const listings = Object.entries(deliveryBuckets?.[DELIVERY_METHOD]?.listings || {}).reduce(
      (acc, [listingId, row]) => {
        if (isPositiveInteger(row?.quantity) && isValidPriceSnapshot(row?.priceSnapshot)) {
          acc[listingId] = {
            quantity: 1,
            priceSnapshot: row.priceSnapshot,
          };
        }
        return acc;
      },
      {}
    );

    return Object.keys(listings).length > 0
      ? {
          ...sellerCart,
          [sellerId]: {
            [DELIVERY_METHOD]: {
              listings,
            },
          },
        }
      : sellerCart;
  }, {});
};

const canUseBrowserStorage = () => {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch (e) {
    return false;
  }
};

const ensureSellerBucket = (cart, sellerId, deliveryMethod) => {
  return {
    ...cart,
    [sellerId]: {
      ...(cart[sellerId] || {}),
      [deliveryMethod]: {
        ...((cart[sellerId] || {})[deliveryMethod] || {}),
        listings: {
          ...(((cart[sellerId] || {})[deliveryMethod] || {}).listings || {}),
        },
      },
    },
  };
};

/**
 * Return a sanitized cart from current user's private data.
 *
 * @param {Object} currentUser
 * @returns {Object}
 */
export const getCartFromCurrentUser = currentUser => {
  return cleanupCart(currentUser?.attributes?.profile?.privateData?.cart || {});
};

export const getGuestCart = () => {
  if (!canUseBrowserStorage()) {
    return {};
  }

  try {
    const storedCart = window.localStorage.getItem(GUEST_CART_STORAGE_KEY);
    return cleanupCart(storedCart ? JSON.parse(storedCart) : {});
  } catch (e) {
    return {};
  }
};

export const storeGuestCart = cart => {
  if (!canUseBrowserStorage()) {
    return cleanupCart(cart);
  }

  const nextCart = cleanupCart(cart);
  try {
    window.localStorage.setItem(GUEST_CART_STORAGE_KEY, JSON.stringify(nextCart));
  } catch (e) {
    // Ignore storage errors. Redux still keeps the cart for the current page session.
  }
  return nextCart;
};

export const clearGuestCart = () => {
  if (!canUseBrowserStorage()) {
    return;
  }

  try {
    window.localStorage.removeItem(GUEST_CART_STORAGE_KEY);
  } catch (e) {
    // Ignore storage errors.
  }
};

/**
 * Create a serializable price snapshot for cart rows.
 *
 * @param {Object} listing
 * @returns {{amount: number, currency: string}|null}
 */
export const getPriceSnapshotFromListing = listing => {
  const price = listing?.attributes?.price;
  return Number.isInteger(price?.amount) && typeof price?.currency === 'string'
    ? { amount: price.amount, currency: price.currency }
    : null;
};

/**
 * Add or replace a cart row. The same listing can only exist once per seller across delivery methods.
 *
 * @param {Object} cart
 * @param {Object} params
 * @returns {Object}
 */
export const setCartListing = (cart, params) => {
  const { sellerId, listingId, deliveryMethod, quantity, priceSnapshot } = params || {};

  if (
    !sellerId ||
    !listingId ||
    deliveryMethod !== DELIVERY_METHOD ||
    !isPositiveInteger(quantity) ||
    !isValidPriceSnapshot(priceSnapshot)
  ) {
    return cleanupCart(cart);
  }

  let nextCart = cleanupCart(cart);
  nextCart = ensureSellerBucket(nextCart, sellerId, deliveryMethod);

  nextCart[sellerId][deliveryMethod].listings[listingId] = {
    quantity: 1,
    priceSnapshot,
  };

  return cleanupCart(nextCart);
};

/**
 * Remove a single cart row.
 *
 * @param {Object} cart
 * @param {Object} params
 * @returns {Object}
 */
export const removeCartListing = (cart, params) => {
  const { sellerId, listingId, deliveryMethod } = params || {};
  const nextCart = cleanupCart(cart);
  const listings = nextCart[sellerId]?.[deliveryMethod]?.listings;

  if (listings?.[listingId]) {
    delete listings[listingId];
  }

  return cleanupCart(nextCart);
};

/**
 * Update quantity of an existing row. Quantity <= 0 removes the row.
 *
 * @param {Object} cart
 * @param {Object} params
 * @returns {Object}
 */
export const updateCartListingQuantity = (cart, params) => {
  const { sellerId, listingId, deliveryMethod, quantity } = params || {};
  const nextCart = cleanupCart(cart);
  const currentRow = nextCart[sellerId]?.[deliveryMethod]?.listings?.[listingId];

  if (!currentRow) {
    return nextCart;
  }

  return quantity > 0
    ? setCartListing(nextCart, {
        sellerId,
        listingId,
        deliveryMethod,
        quantity,
        priceSnapshot: currentRow.priceSnapshot,
      })
    : removeCartListing(nextCart, { sellerId, listingId, deliveryMethod });
};

/**
 * Remove purchased rows from one seller + delivery scope.
 *
 * @param {Object} cart
 * @param {Object} params
 * @returns {Object}
 */
export const clearPurchasedScope = (cart, params) => {
  const { sellerId, deliveryMethod, listingIds = [] } = params || {};
  let nextCart = cleanupCart(cart);

  listingIds.forEach(listingId => {
    nextCart = removeCartListing(nextCart, { sellerId, deliveryMethod, listingId });
  });

  return cleanupCart(nextCart);
};

/**
 * Check if a listing exists in the cart for a seller.
 *
 * @param {Object} cart
 * @param {string} sellerId
 * @param {string} listingId
 * @returns {boolean}
 */
export const isListingInCart = (cart, sellerId, listingId) => {
  const sellerBuckets = cleanupCart(cart)[sellerId] || {};
  return !!sellerBuckets[DELIVERY_METHOD]?.listings?.[listingId];
};

/**
 * Count distinct seller cards.
 *
 * @param {Object} cart
 * @returns {number}
 */
export const getCartCount = cart => {
  return Object.keys(cleanupCart(cart)).length;
};

/**
 * Return flat cart entries in stable seller/delivery/listing order.
 *
 * @param {Object} cart
 * @returns {Array}
 */
export const getCartEntries = cart => {
  const nextCart = cleanupCart(cart);

  return Object.entries(nextCart).flatMap(([sellerId, sellerBuckets]) => {
    const listings = sellerBuckets[DELIVERY_METHOD]?.listings || {};
    return Object.entries(listings).map(([listingId, row]) => {
      return {
        sellerId,
        deliveryMethod: DELIVERY_METHOD,
        listingId,
        quantity: row.quantity,
        priceSnapshot: row.priceSnapshot,
      };
    });
  });
};

/**
 * Create checkout payload shape expected by the cart checkout flow.
 *
 * @param {Array} cartEntries
 * @returns {Object}
 */
export const buildProviderCart = cartEntries => {
  return cartEntries.reduce((providerCart, entry) => {
    return {
      ...providerCart,
      [entry.listingId]: {
        quantity: 1,
      },
    };
  }, {});
};

export const mergeCarts = (baseCart, cartToMerge) => {
  return getCartEntries(cartToMerge).reduce((mergedCart, entry) => {
    return setCartListing(mergedCart, entry);
  }, cleanupCart(baseCart));
};

export const sanitizeCart = cleanupCart;
